import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GradeBadge, AccessBadge } from "@/components/n360/Badges";
import { ROLE_LABEL } from "@/lib/content";
import { LayoutDashboard, Users, UserCircle, GitPullRequestArrow, ShieldQuestion, Crown, LogOut } from "lucide-react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-dashboard", roles: "all" },
  { to: "/app/directory", label: "Directory", icon: Users, testid: "sidebar-directory", roles: "all" },
  { to: "/app/profile", label: "My Dossier", icon: UserCircle, testid: "sidebar-profile", roles: "all" },
  { to: "/app/referrals", label: "Referrals", icon: GitPullRequestArrow, testid: "sidebar-referrals", roles: "all" },
  { to: "/app/intelligence", label: "Intelligence Desk", icon: ShieldQuestion, testid: "sidebar-intelligence", roles: ["intelligence_contributor", "field_associate", "admin"] },
  { to: "/app/admin", label: "Sovereign Oversight", icon: Crown, testid: "sidebar-admin", roles: ["admin"] },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const visible = NAV.filter((n) => n.roles === "all" || (user && n.roles.includes(user.role)));

  const onLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="n360-app-bg n360-grain flex">
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-[#F1551D]/15 bg-[#FFFFFF]/80 backdrop-blur-xl sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2.5 px-6 h-16 border-b border-[#F1551D]/15">
          <span className="w-8 h-8 rounded-lg border border-[#F1551D]/50 grid place-items-center font-display text-[#F1551D] text-sm">N</span>
          <span className="font-display text-lg text-[#0D3C4C]">N<span className="gold-text">360</span></span>
        </Link>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {visible.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${active ? "bg-[#F1551D]/12 text-[#B84214] border border-[#F1551D]/30" : "text-[#5f7d86] hover:text-[#0D3C4C] hover:bg-white/5 border border-transparent"}`}>
                <Icon className="w-4 h-4" /> {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-[#F1551D]/15">
          <div className="text-sm text-[#154859] truncate">{user?.name}</div>
          <div className="text-[11px] text-[#8598a0] mb-2">{ROLE_LABEL[user?.role] || user?.role}</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            <GradeBadge grade={user?.grade} />
            <AccessBadge level={user?.access_level} />
          </div>
          <button onClick={onLogout} data-testid="sidebar-logout" className="flex items-center gap-2 text-xs text-[#5f7d86] hover:text-rose-400 transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 relative z-10">
        <div className="md:hidden flex items-center justify-between px-5 h-14 border-b border-[#F1551D]/15 bg-[#FFFFFF]/90 sticky top-0 z-20">
          <span className="font-display text-lg text-[#0D3C4C]">N<span className="gold-text">360</span></span>
          <button onClick={onLogout} className="text-xs text-[#5f7d86]">Sign out</button>
        </div>
        <div className="md:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-[#F1551D]/10 bg-[#FFFFFF]/70 sticky top-14 z-10">
          {visible.map((n) => (
            <Link key={n.to} to={n.to} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs ${loc.pathname === n.to ? "bg-[#F1551D]/15 text-[#B84214]" : "text-[#5f7d86]"}`}>{n.label}</Link>
          ))}
        </div>
        <div className="p-5 sm:p-8 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
